import { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  AlertTitle,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';

import { Button, IconButton, RadioGroup } from '@components/base';
import { IndexName } from '@enums/IndexName';
import useFilterDefsQuery from '@hooks/queryHooks/useFilterDefsQuery';
import useConfirmDialog from '@hooks/useConfirmDialog';
import useSnack from '@hooks/useSnack';
import useSearchAndFilter from '@modules/layouts/hooks/useSearchAndFilter';
import { getCompatibilityMap } from '@utils/searchUtils';

const ConfigureSearch: FC = () => {
  const { t } = useTranslation();
  const { searchQuery, updateIndexName } = useSearchAndFilter();
  const { addSnack } = useSnack();
  const [dialogOpen, setDialogOpen] = useState(false);

  const [loading, setLoading] = useState(false);
  const [indexName, setIndexName] = useState(searchQuery.indexName);

  const { data: filterDefs } = useFilterDefsQuery(indexName);

  const closeDialog = () => {
    setDialogOpen(false);
    setIndexName(searchQuery.indexName);
  };

  const { dialog, request } = useConfirmDialog();

  const onConfirm = async () => {
    setLoading(true);
    const compatibilityMap = getCompatibilityMap(filterDefs, searchQuery);
    setLoading(false);

    if (!compatibilityMap) addSnack(t('somethingWentWrong'));
    else if (
      Object.values(compatibilityMap).every(Boolean) ||
      (await request(
        t('search.version.compatibilityError.title'),
        <Stack spacing={2}>
          {Object.entries(compatibilityMap).map(([type, canConvert]) =>
            canConvert ? null : (
              <Alert key={type} severity='warning' variant='standard'>
                <AlertTitle>
                  {t('search.version.compatibilityError.title', { context: type })}
                </AlertTitle>
                {t('search.version.compatibilityError.description', { context: type })}
              </Alert>
            )
          )}
          <Typography>Do you wish to update search configuration?</Typography>
        </Stack>,
        'yes'
      ))
    ) {
      updateIndexName?.(indexName, compatibilityMap);
      setDialogOpen(false);
    }
  };

  return (
    <Fragment>
      <IconButton
        color='secondary'
        icon='tune'
        onClick={() => setDialogOpen(true)}
        title='Configure search'
      />

      <Dialog
        component='form'
        fullWidth
        maxWidth='xs'
        onClose={loading ? undefined : closeDialog}
        onSubmit={e => {
          e.preventDefault();
          onConfirm();
        }}
        open={dialogOpen}
      >
        <DialogTitle>{t('search.configure')}</DialogTitle>

        <DialogContent>
          <RadioGroup
            label={t('search.index.label')}
            margin='dense'
            onChange={({ target: { value } }) => setIndexName(value as IndexName)}
            options={Object.values(IndexName).map(searchIndex => ({
              label: t('search.index', { context: searchIndex }),
              value: searchIndex,
              helperText: t('search.index.description', { context: searchIndex }),
            }))}
            required
            value={indexName}
          />
        </DialogContent>

        <DialogActions>
          <Button disabled={loading} onClick={closeDialog}>
            {t('cancel')}
          </Button>

          <Button loading={loading} type='submit' variant='contained'>
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {dialog}
    </Fragment>
  );
};

export default ConfigureSearch;
